import request from '#api/axios';
import { queryKeyFactory } from '#api/queryKeyFactory';
import { useDeleteScheduleCoachMutation } from '#hooks/queries/schedules/useDeleteScheduleCoachMutation';
import { IGetCoachSchedules } from '#types/api';
import {
  GropuRegisteredCoachScheduleDetail,
  IGetCoachSchedule,
  IGetCoachSchedulePerWeek,
  PayloadLecture,
  Schedule
} from '#types/schedule';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const getCoachSchedules = async (
  coachId: string,
  startAt: string,
  gymId?: string
) => {
  const res = await request<IGetCoachSchedules>({
    method: 'get',
    url: `/schedules`,
    params: {
      coachId,
      gymId,
      startAt
    }
  });

  return res.data;
};

export const useGetCoachSchedules = (
  coachId: string,
  startAt: string,
  gymId?: string
) => {
  return useQuery(
    queryKeyFactory.GET_COACH_SCHEDULES(coachId, startAt, gymId),
    () => getCoachSchedules(coachId, startAt, gymId)
  );
};

export const postCoachSchedules = async (payload: Schedule[]) => {
  const res = await request({
    method: 'post',
    url: '/schedules',
    data: payload
  });

  return res.data;
};

export const usePostCoachSchedules = () => {
  return useMutation({
    mutationFn: postCoachSchedules
  });
};

export const postImpossibleSchedules = async (at: string) => {
  const res = await request({
    method: 'post',
    url: `/impossible-schedules`,
    data: { at }
  });

  return res.data;
};

export const getCoachSchedule = async ({
  coachId,
  gymId,
  startAt
}: {
  coachId: string;
  gymId?: string;
  startAt: string;
}) => {
  const res = await request<IGetCoachSchedule>({
    method: 'get',
    url: `/schedules`,
    params: {
      coachId,
      gymId,
      startAt
    }
  });

  return res.data;
};

export const deleteImpossibleSchedules = async (id: string) => {
  const res = await request({
    method: 'delete',
    url: `/impossible-schedules/${id}`
  });

  return res.data;
};

export const useGetCoachSchedule = ({
  coachId,
  gymId,
  startAt
}: {
  coachId: string;
  gymId?: string;
  startAt: string;
}) => {
  return useQuery(
    queryKeyFactory.GET_COACH_SCHEDULE({ coachId, gymId, startAt }),
    () => getCoachSchedule({ coachId, gymId, startAt }),
    { enabled: !!coachId }
  );
};

export const deleteCoachSchedule = async () => {
  const res = await request<IGetCoachSchedule>({
    method: 'delete',
    url: `/schedules`
  });

  return res.data;
};

export const getCoachSchedulePerWeek = async ({
  startAt,
  durationDays
}: {
  startAt: string;
  durationDays: number;
}) => {
  const res = await request<IGetCoachSchedulePerWeek>({
    method: 'get',
    params: {
      startAt: startAt,
      durationDays: durationDays ? 7 : durationDays
    },
    url: `/schedules/coach`
  });
  return res.data;
};

export const postScheduleCoach = async (payload: PayloadLecture) => {
  const res = await request({
    url: `/schedules/coach`,
    method: 'POST',
    data: payload
  });
  return res.data;
};

export const usePostScheduleCoach = (
  options?: UseMutationOptions<unknown, AxiosError, PayloadLecture>
) => {
  return useMutation(postScheduleCoach, options);
};

const postScheduleCoachGroup = async ({
  lectureId,
  payload
}: {
  lectureId: string;
  payload: PayloadLecture[];
}) => {
  const res = await request<{
    success: PayloadLecture[];
    failure: PayloadLecture[];
  }>({
    url: `/schedules/coach/group/${lectureId}`,
    method: 'POST',
    data: payload.map(item => ({
      ...item,
      repetition: null
    }))
  });

  return res.data;
};

export const usePostScheduleCoachGroup = (
  options?: UseMutationOptions<
    {
      success: PayloadLecture[];
      failure: PayloadLecture[];
    },
    AxiosError,
    {
      lectureId: string;
      payload: PayloadLecture[];
    }
  >
) => {
  return useMutation(postScheduleCoachGroup, options);
};

/**
 * 그룹 수업 상세 조회
 * @param lectureId 수업 id
 * @param taughtAt YYYY-MM-DDTHH:mm:ssZ UTC 포맷
 * @returns
 */
export const getScheduleCoachGroup = async ({
  lectureId,
  taughtAt
}: {
  lectureId: string;
  taughtAt: string;
}) => {
  const res = await request<GropuRegisteredCoachScheduleDetail[]>({
    url: `/schedules/coach/group/${lectureId}?taughtAt=${taughtAt}`,
    method: 'GET',
    data: {
      lectureId,
      taughtAt
    }
  });

  return res.data;
};

export const useGetScheduleCoachGroup = (
  { lectureId, taughtAt }: { lectureId: string; taughtAt: string },
  options?: UseQueryOptions<
    GropuRegisteredCoachScheduleDetail[],
    AxiosError,
    GropuRegisteredCoachScheduleDetail[],
    string[]
  >
) => {
  return useQuery(
    [`/schedules/coach/group/${lectureId}`, taughtAt],
    () => getScheduleCoachGroup({ lectureId, taughtAt }),
    options
  );
};

/**
 * 그룹 수업 삭제 api가 없어서 그룹 수업 상세 조회후 각 수업을 삭제하는 방식으로 대체
 * 모든 회원의 수업을 삭제
 */
export const useDeleteScheduleCoachGroup = ({
  lectureId,
  taughtAt,
  onSuccess,
  onError
}: {
  lectureId: string;
  taughtAt: string;
  onSuccess: () => void;
  onError: () => void;
}) => {
  const { data } = useGetScheduleCoachGroup(
    { lectureId, taughtAt },
    {
      enabled: !!lectureId && !!taughtAt
    }
  );

  const { mutate } = useDeleteScheduleCoachMutation();

  const deleteScheduleCoachGroup = async () => {
    if (!data) return;

    try {
      await Promise.all(
        data.map(item => {
          mutate({ id: item.id, status: 'WITHDRAWAL' });
        })
      );

      onSuccess();
    } catch (e) {
      onError();
    }
  };

  return { deleteScheduleCoachGroup };
};

type PatchScheduleCoachGroupParams = {
  preTaughtAt: string;
  newCoachSchedules: PayloadLecture[];
};
const patchScheduleCoachGroup = async (
  lectureId: string,
  payload: PatchScheduleCoachGroupParams
) => {
  const res = await request({
    url: `/schedules/coach/group/${lectureId}`,
    method: 'PATCH',
    data: payload
  });

  return res.data;
};

type UsePatchScheduleCoachGroupMutationOptions = UseMutationOptions<
  any,
  AxiosError,
  { lectureId: string; payload: PatchScheduleCoachGroupParams }
>;

export const usePatchScheduleCoachGroup = (
  options?: UsePatchScheduleCoachGroupMutationOptions
) => {
  return useMutation(
    async ({
      lectureId,
      payload
    }: {
      lectureId: string;
      payload: PatchScheduleCoachGroupParams;
    }) => {
      return await patchScheduleCoachGroup(lectureId, payload);
    },
    options
  );
};
